import { CheckCircleIcon } from "@heroicons/react/24/outline";
import clsx from "clsx";
import { useMemo, useState } from "react";

import Button from "~/components/buttons/Button/Button.tsx";
import { ComboBoxPlain } from "~/components/formElements/ComboBox.tsx";
import Hint from "~/components/Hint";
import SidebarBusyOverlay from "~/components/Sidebar/components/SidebarBusyOverlay.tsx";
import SidebarContent from "~/components/Sidebar/components/SidebarContent.tsx";
import SidebarFooter from "~/components/Sidebar/components/SidebarFooter.tsx";
import SidebarHeader from "~/components/Sidebar/components/SidebarHeader.tsx";
import SidebarHeaderHeadline from "~/components/Sidebar/components/SidebarHeaderHeadline.tsx";
import type { EligibleQuotaInvoice } from "~/modules/quota/api/quota/quotaTypes.ts";
import { QuotaInvoiceSummary } from "~/modules/quota/components/QuotaInvoiceSummary.tsx";
import { formatQuotaInvoiceProjectLabel } from "~/modules/quota/utils/quotaInvoiceUtils.ts";

type Props = {
	invoices: EligibleQuotaInvoice[] | undefined;
	isLoading: boolean;
	excludedInvoiceIds?: string[];
	onCancel: () => void;
	onConfirm: (invoiceIds: string[]) => void;
};

// Select one or more invoices with an excess to back an invoice-backed quota.
// An excluded invoice (already selected in the form) does not show.
export const QuotaInvoiceSelector = ({ invoices, isLoading, excludedInvoiceIds, onCancel, onConfirm }: Props) => {
	const [selectedProjectId, setSelectedProjectId] = useState<string>("");
	const [selectedInvoiceIds, setSelectedInvoiceIds] = useState<string[]>([]);

	const selectableInvoices = useMemo(() => {
		return (invoices ?? []).filter(invoice => !excludedInvoiceIds?.includes(invoice.id));
	}, [invoices, excludedInvoiceIds]);

	const projectSelectOptions = useMemo(() => {
		const options = new Map<string, string>();
		selectableInvoices.forEach(invoice => {
			if (invoice.projectId !== null && !options.has(invoice.projectId)) {
				options.set(invoice.projectId, formatQuotaInvoiceProjectLabel(invoice));
			}
		});
		return [...options].map(([value, label]) => ({ value, label }));
	}, [selectableInvoices]);

	// Show every invoice by default. The combobox filters the list down to one project.
	const visibleInvoices = useMemo(() => {
		return selectedProjectId
			? selectableInvoices.filter(invoice => invoice.projectId === selectedProjectId)
			: selectableInvoices;
	}, [selectableInvoices, selectedProjectId]);

	const handleInvoiceClick = (invoiceId: string) => {
		setSelectedInvoiceIds(prevState => {
			if (prevState.includes(invoiceId)) {
				return prevState.filter(id => id !== invoiceId);
			}
			return [...prevState, invoiceId];
		});
	};

	const handleConfirm = () => {
		if (selectedInvoiceIds.length === 0) {
			return;
		}
		onConfirm(selectedInvoiceIds);
	};

	return <>
		<SidebarHeader>
			<SidebarHeaderHeadline>Rechnungen auswählen</SidebarHeaderHeadline>
		</SidebarHeader>
		<SidebarContent>
			{isLoading && <SidebarBusyOverlay />}
			<div className="flex items-center justify-center gap-2 border-b border-gray-200 pb-6">
				<div className="flex-1">
					<ComboBoxPlain optionsData={projectSelectOptions}
								   allowNew={false}
								   placeholder="Nach Projekt filtern"
								   onChange={(value) => setSelectedProjectId(typeof value === "string" ? value : "")}
								   value={selectedProjectId} />
				</div>
				<Button theme="primary"
						disabled={!selectedProjectId}
						onClick={() => setSelectedProjectId("")}>
					Zurücksetzen
				</Button>
			</div>
			<div className="mt-4 space-y-2">
				{!isLoading && visibleInvoices.map(invoice => {
					const isSelected = selectedInvoiceIds.includes(invoice.id);
					return <button key={invoice.id}
								   type="button"
								   className="group grid w-full grid-cols-[auto_1fr] rounded-lg text-sm hover:bg-gray-100"
								   onClick={() => handleInvoiceClick(invoice.id)}>
						<div className={clsx("flex w-10 flex-col items-center justify-center rounded-l-lg border",
							isSelected ? "border-success-600 bg-success-600" : "border-gray-300")}>
							{isSelected ?
								<CheckCircleIcon className="size-6 text-white" /> :
								<div className="size-5 rounded-full border border-gray-300" />}
						</div>
						<div className="flex min-w-0 rounded-r-lg border border-l-0 border-gray-300 px-4 py-3">
							<QuotaInvoiceSummary invoice={invoice} />
						</div>
					</button>;
				})}
				{!isLoading && selectableInvoices.length === 0 &&
					<Hint>
						<p>Es gibt keine Rechnungen mit Überschuss, die einem Abrechnungskontingent zugewiesen werden
							können.</p>
					</Hint>}
			</div>
		</SidebarContent>
		<SidebarFooter>
			<Button disabled={selectedInvoiceIds.length === 0}
					onClick={handleConfirm}>
				{`${selectedInvoiceIds.length > 0 ? selectedInvoiceIds.length + " " : ""}Rechnung${selectedInvoiceIds.length > 1 ? "en" : ""} auswählen`}
			</Button>
			<Button theme="white"
					onClick={onCancel}>
				Zurück
			</Button>
		</SidebarFooter>
	</>;
};
